import { motion } from "framer-motion";
import { Mail, MessageCircle, ExternalLink } from "lucide-react";
import { MaskedLines } from "./MaskedText";
import Marquee from "./Marquee";

const WHATSAPP_NUMBER = "919831000000"; // display + wa.me link only, no automation
const SUPPORT_EMAIL = "support@sonarbanglahaat.com";

export default function Slide5Success({ ownerName = "" }) {
  return (
    <div className="w-full flex flex-col items-center text-center" data-testid="slide-5-success">
      {/* Animated checkmark */}
      <motion.div
        initial={{ opacity: 0, scale: 0.85 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
        className="relative w-24 h-24 md:w-32 md:h-32"
      >
        <motion.svg
          viewBox="0 0 100 100"
          className="w-full h-full"
          initial="hidden"
          animate="visible"
        >
          <motion.circle
            cx="50"
            cy="50"
            r="46"
            fill="none"
            stroke="#A73A22"
            strokeWidth="1.4"
            variants={{
              hidden: { pathLength: 0, opacity: 0 },
              visible: {
                pathLength: 1,
                opacity: 1,
                transition: { duration: 1.1, ease: [0.22, 1, 0.36, 1] },
              },
            }}
          />
          <motion.path
            d="M30 52 L45 66 L72 38"
            fill="none"
            stroke="#A73A22"
            strokeWidth="2.4"
            strokeLinecap="square"
            variants={{
              hidden: { pathLength: 0 },
              visible: {
                pathLength: 1,
                transition: { duration: 0.9, ease: [0.22, 1, 0.36, 1], delay: 0.6 },
              },
            }}
          />
        </motion.svg>
      </motion.div>

      <div className="mt-8 eyebrow">Chapter 05 · Received</div>
      <h2 className="mt-3 font-editorial text-[36px] sm:text-[44px] md:text-[56px] leading-[1.05] tracking-[-0.01em] max-w-3xl">
        <MaskedLines
          lines={[
            `Thank you, ${ownerName || "friend"}.`,
            <span key="i" className="italic text-[color:var(--sbh-terracotta)]">Your application is in.</span>,
          ]}
        />
      </h2>
      <motion.p
        className="mt-5 max-w-xl text-[15px] md:text-[16px] leading-relaxed text-[color:var(--sbh-ink-2)]"
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.8, duration: 0.7 }}
      >
        Our team will review your details and documents, and reach out within 24 hours.
      </motion.p>

      <motion.ol
        className="mt-10 md:mt-14 max-w-2xl w-full text-left grid gap-6"
        initial="hidden"
        animate="visible"
        variants={{
          hidden: {},
          visible: { transition: { staggerChildren: 0.15, delayChildren: 1.0 } },
        }}
      >
        {[
          {
            n: "01",
            t: "We verify your documents",
            b: "Our onboarding team reviews PAN, GST and banking documents privately.",
          },
          {
            n: "02",
            t: "Approval — then a private link for bank details",
            b: "Once approved, you'll receive a private secure link to add your payout bank details.",
          },
          {
            n: "03",
            t: "Your storefront goes live in 48–72 hours",
            b: "We'll personally guide you through your first product listing.",
          },
        ].map((step) => (
          <motion.li
            key={step.n}
            className="grid grid-cols-[auto_1fr] gap-6 border-t border-[color:var(--sbh-border)] pt-5"
            variants={{
              hidden: { opacity: 0, y: 20 },
              visible: { opacity: 1, y: 0, transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] } },
            }}
          >
            <span className="font-editorial text-[36px] leading-none text-[color:var(--sbh-mustard)]">
              {step.n}
            </span>
            <div>
              <div className="font-editorial text-[20px] text-[color:var(--sbh-ink)]">
                {step.t}
              </div>
              <div className="mt-1 text-[14px] text-[color:var(--sbh-ink-2)] leading-relaxed">
                {step.b}
              </div>
            </div>
          </motion.li>
        ))}
      </motion.ol>

      <motion.div
        className="mt-14 md:mt-20 w-full max-w-3xl border-t border-[color:var(--sbh-border)] pt-8 flex flex-col md:flex-row items-center justify-between gap-4"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6, duration: 0.7 }}
      >
        <a
          href={`https://wa.me/${WHATSAPP_NUMBER}`}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 text-[13px] tracking-[0.16em] uppercase text-[color:var(--sbh-ink)] hover:text-[color:var(--sbh-terracotta)] transition-colors link-underline"
          data-testid="success-whatsapp-link"
        >
          <MessageCircle className="w-4 h-4" strokeWidth={1.7} />
          WhatsApp Support
        </a>
        <a
          href={`mailto:${SUPPORT_EMAIL}`}
          className="inline-flex items-center gap-2 text-[13px] tracking-[0.16em] uppercase text-[color:var(--sbh-ink)] hover:text-[color:var(--sbh-terracotta)] transition-colors link-underline"
          data-testid="success-email-link"
        >
          <Mail className="w-4 h-4" strokeWidth={1.7} />
          {SUPPORT_EMAIL}
        </a>
        <a
          href="https://sonarbanglahaat.com"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 text-[13px] tracking-[0.16em] uppercase text-[color:var(--sbh-ink)] hover:text-[color:var(--sbh-terracotta)] transition-colors link-underline"
          data-testid="success-site-link"
        >
          Back to sonarbanglahaat.com
          <ExternalLink className="w-4 h-4" strokeWidth={1.7} />
        </a>
      </motion.div>

      <div className="w-full mt-12 border-t border-[color:var(--sbh-border)] pt-4 pb-2">
        <Marquee />
      </div>
    </div>
  );
}
